package co.uk.paxes.pinpoint.model

import kotlinx.serialization.Serializable
import kotlin.math.abs

/**
 * Represents a position on the PINPOINT board using section-based coordinates
 * The board has 9 sections (3x3), each section has up to 16 positions (4x4)
 */
@Serializable
data class Position(
    val sectionRow: Int,    // 0-2: which row of sections
    val sectionCol: Int,    // 0-2: which column of sections  
    val localRow: Int,      // 0-3: row within the section
    val localCol: Int       // 0-3: column within the section
) {
    init {
        require(sectionRow in 0..2) { "Section row must be between 0 and 2" }
        require(sectionCol in 0..2) { "Section column must be between 0 and 2" }
        require(localRow in 0..3) { "Local row must be between 0 and 3" }
        require(localCol in 0..3) { "Local column must be between 0 and 3" }
    }
    
    companion object {
        /**
         * Create position from legacy row/col coordinates (0-9 each for 10x10 board)
         */
        fun fromLegacy(row: Int, col: Int): Position {
            require(row in 0..9) { "Legacy row must be between 0 and 9" }
            require(col in 0..9) { "Legacy column must be between 0 and 9" }
            
            val sectionRow = when (row) {
                in 0..3 -> 0
                in 4..6 -> 1
                in 7..9 -> 2
                else -> 2
            }
            
            val sectionCol = when (col) {
                in 0..2 -> 0
                in 3..6 -> 1
                in 7..9 -> 2
                else -> 2
            }

            val localRow = when (row) {
                in 0..3 -> row
                in 4..6 -> row - 4
                in 7..9 -> row - 7
                else -> 0
            }
            
            val localCol = when (col) {
                in 0..2 -> col
                in 3..6 -> col - 3
                in 7..9 -> col - 7
                else -> 0
            }
            
            return Position(sectionRow, sectionCol, localRow, localCol)
        }
    }
    
    /**
     * Convert to legacy row/col coordinates for backward compatibility
     */
    fun toLegacy(): Pair<Int, Int> {
        val row = when (sectionRow) {
            0 -> localRow
            1 -> 4 + localRow
            2 -> 7 + localRow
            else -> 0
        }
        
        val col = when (sectionCol) {
            0 -> localCol
            1 -> 3 + localCol
            2 -> 7 + localCol
            else -> 0
        }
        
        return Pair(row, col)
    }
    
    /**
     * Check if this position is in the same section as another
     */
    fun isInSameSection(other: Position): Boolean {
        return sectionRow == other.sectionRow && sectionCol == other.sectionCol
    }
    
    /**
     * Check if this position is adjacent to another position
     */
    fun isAdjacentTo(other: Position): Boolean {
        val (thisRow, thisCol) = toLegacy()
        val (otherRow, otherCol) = other.toLegacy()
        
        val rowDiff = abs(thisRow - otherRow)
        val colDiff = abs(thisCol - otherCol)
        return (rowDiff <= 1 && colDiff <= 1) && (rowDiff + colDiff > 0)
    }
    
    /**
     * Check if this position is valid (within board bounds)
     */
    fun isValid(): Boolean = sectionRow in 0..2 && sectionCol in 0..2 && 
                            localRow in 0..3 && localCol in 0..3
    
    /**
     * Get all adjacent positions within the board
     */
    fun getAdjacentPositions(): List<Position> {
        val (thisRow, thisCol) = toLegacy()
        val adjacent = mutableListOf<Position>()
        
        for (r in (thisRow - 1)..(thisRow + 1)) {
            for (c in (thisCol - 1)..(thisCol + 1)) {
                if (r in 0..9 && c in 0..9 && (r != thisRow || c != thisCol)) {
                    adjacent.add(fromLegacy(r, c))
                }
            }
        }
        return adjacent
    }
    
    /**
     * Get section identifier
     */
    fun getSectionId(): String = "${sectionRow}_${sectionCol}"
    
    override fun toString(): String {
        return "Position(section=$sectionRow,$sectionCol, local=$localRow,$localCol)"
    }
}
